/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-empty-interface */
/* eslint-disable react/no-unstable-nested-components */
import type { PopoverContentProps } from "@radix-ui/react-popover";
import { TemplatesEnum } from "components/financials/modern/Templates";
import type { IFinancialsTemplatesQuery } from "components/financials/modern/graphql/__generated__/FinancialsTemplatesQuery.generated";
import type { IFinancialsTemplateFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/FinancialsTemplateFieldsFragment.generated";
import { Button } from "components/ui/Button";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/Popover";
import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import usePagination from "lib/hooks/usePagination";
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Drawer } from "vaul";

interface QuickTemplateSwitcherProps {
  templates: IFinancialsTemplatesQuery["financialsTemplates"]["nodes"];
  offerableTemplates?: IFinancialsTemplatesQuery["financialsTemplates"]["nodes"];
  renderingTemplate?: IFinancialsTemplateFieldsFragment | null;
  side?: PopoverContentProps["side"];
  sideOffset?: PopoverContentProps["sideOffset"];
  setIsSheetOpen: (isOpen: boolean) => void;
  setSelectedTab: (tab: TemplatesEnum) => void;
  onChange?: (template: IFinancialsTemplateFieldsFragment | null) => void;
}
export default function QuickTemplateSwitcher({
  templates,
  offerableTemplates = [],
  renderingTemplate,
  side = "bottom",
  sideOffset = 12,
  setIsSheetOpen,
  setSelectedTab,
  onChange,
}: QuickTemplateSwitcherProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const starredTemplates = templates || [];

  return (
    <>
      <Drawer.Root open={isDrawerOpen} onOpenChange={(nextOpen) => setDrawerOpen(nextOpen)}>
        <Drawer.Trigger asChild={true}>
          <button
            type="button"
            className={classNames(
              "inline-flex lg:hidden",
              "relative -ml-px h-12 items-center bg-slate-50/60 p-2 px-4 text-slate-950 md:h-10 md:px-2 ",
              "border border-slate-200",
              {
                "bg-violet-50 text-violet-600 dark:bg-violet-600/20 dark:text-violet-200": false,
              },
              "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
              "focus-visible:outline-none",
              "focus-visible:ring-1 focus-visible:ring-offset-1",
              "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
              "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
              "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            <ChevronDownIcon className="-ml-px size-5" absoluteStrokeWidth={true} strokeWidth={3} />
          </button>
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 z-50 bg-black/50" onClick={() => setDrawerOpen(false)} />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 mt-24 flex flex-col rounded-t-xl bg-zinc-100 dark:bg-transparent">
            <div className="flex-1 rounded-t-2xl bg-white p-4 dark:bg-slate-800">
              <div className="mx-auto mb-6 h-1.5 w-12 flex-shrink-0 rounded-full bg-zinc-300" />

              <div className="mx-auto max-w-lg">
                <TemplatesList
                  templates={starredTemplates}
                  offerableTemplates={offerableTemplates}
                  renderingTemplate={renderingTemplate}
                  onApplyTemplate={(template) => {
                    setDrawerOpen(false);
                    onChange?.(template);
                  }}
                  setIsSheetOpen={setIsSheetOpen}
                  setSelectedTab={setSelectedTab}
                />
              </div>
            </div>
          </Drawer.Content>
          <Drawer.Overlay />
        </Drawer.Portal>
      </Drawer.Root>

      <Popover open={isPopoverOpen} onOpenChange={(nextOpen) => setIsPopoverOpen(nextOpen)}>
        <PopoverTrigger asChild={true}>
          <button
            type="button"
            className={classNames(
              "hidden lg:inline-flex",
              "relative -ml-px h-10 items-center bg-slate-50/60 p-2 text-slate-950",
              "border border-slate-200",
              "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
              "focus-visible:outline-none",
              "focus-visible:ring-1 focus-visible:ring-offset-1",
              "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
              "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
              "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            <ChevronDownIcon className="-ml-px h-4 w-4" absoluteStrokeWidth={true} strokeWidth={3} />
          </button>
        </PopoverTrigger>

        <PopoverContent
          side={side}
          sideOffset={sideOffset}
          hideWhenDetached={true}
          avoidCollisions={true}
          onOpenAutoFocus={(event) => {
            // See: https://github.com/radix-ui/primitives/discussions/935
            event.preventDefault();
          }}
          className={classNames(
            "w-64 space-y-2 overflow-hidden rounded-xl border border-slate-300 p-2",
            "shadow-lg shadow-slate-900/10",
            "dark:border-slate-950 dark:shadow-neutral-950/40",
            {
              "border-slate-900/80 bg-slate-800 text-slate-300 shadow-neutral-950/40": theme === "dark",
            },
          )}
        >
          <TemplatesList
            templates={starredTemplates}
            offerableTemplates={offerableTemplates}
            setIsSheetOpen={setIsSheetOpen}
            setSelectedTab={setSelectedTab}
            onApplyTemplate={(template) => {
              setIsPopoverOpen(false);
              onChange?.(template);
            }}
          />
        </PopoverContent>
      </Popover>
    </>
  );
}

interface TemplatesListProps {
  templates: IFinancialsTemplateFieldsFragment[];
  offerableTemplates?: IFinancialsTemplateFieldsFragment[];
  renderingTemplate?: IFinancialsTemplateFieldsFragment | null;
  onApplyTemplate?: (template: IFinancialsTemplateFieldsFragment | null) => void;
  setIsSheetOpen: (isOpen: boolean) => void;
  setSelectedTab: (tab: TemplatesEnum) => void;
}
function TemplatesList({
  templates,
  offerableTemplates = [],
  renderingTemplate,
  onApplyTemplate,
  setIsSheetOpen,
  setSelectedTab,
}: TemplatesListProps) {
  const pagination = usePagination({
    items: [...templates, ...offerableTemplates],
    itemsPerPage: 5,
    mode: "slice",
  });

  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-y-2">
        {pagination.currentItems.map((template) => {
          const isActive = renderingTemplate && template.objectId === renderingTemplate.objectId;
          return (
            <button
              type="button"
              key={template.objectId}
              data-active={isActive || undefined}
              className={classNames(
                "flex items-center justify-between rounded-xl p-2 px-2",
                "hover:bg-slate-100 dark:hover:bg-slate-950",
                { "bg-indigo-50/50 dark:bg-indigo-700/10": isActive },
                "group",
                "bg-slate-50 p-3 dark:bg-slate-900",
              )}
              onClick={() => {
                const templateBeingApplied = isActive ? null : template;
                if (onApplyTemplate) {
                  onApplyTemplate(templateBeingApplied);
                }
              }}
            >
              <div className="flex flex-col gap-y-1">
                <strong
                  className={classNames(
                    "line-clamp-1 text-left text-sm font-semibold",
                    "text-gray-700 group-data-[active]:text-indigo-600",
                    "dark:text-slate-200 dark:group-data-[active]:text-indigo-300",
                    "dark:group-hover:text-teal-400 dark:group-hover:group-data-[active]:text-teal-400",
                  )}
                >
                  {template.name}
                </strong>
                <p className="line-clamp-1 text-left text-xs italic tracking-wide text-slate-600 dark:text-slate-400">
                  {template.description}
                </p>
              </div>

              <div aria-hidden={true} className="px-2 text-right">
                <CheckCircleIcon
                  className={classNames(
                    "h-5 w-5 fill-gray-50 text-gray-700 dark:fill-indigo-600/20 dark:text-indigo-300",
                    "group-hover:fill-indigo-50 group-hover:text-indigo-600",
                    "dark:group-hover:fill-teal-600/20 dark:group-hover:text-teal-300",
                    "hidden group-data-[active]:flex",
                  )}
                  absoluteStrokeWidth={true}
                  strokeWidth={2}
                />
              </div>
            </button>
          );
        })}

        {templates.length === 0 && (
          <div className="flex flex-col justify-center space-y-1 p-1 py-2 text-center text-sm">
            <Button
              variant="ghost"
              className="rounded-md p-2 font-medium text-blue-600 hover:bg-indigo-50/80 dark:bg-blue-600/20 dark:text-blue-300"
              onClick={() => {
                setSelectedTab(TemplatesEnum.Public);
                setIsSheetOpen(true);
              }}
            >
              <FormattedMessage
                id="DpHxa5"
                defaultMessage="Confira todos os templates"
                description="Link to templates page that shows up in the quick switcher"
              />
            </Button>
          </div>
        )}
      </div>

      <div
        className={classNames("flex flex-1 items-center space-x-2", {
          hidden: templates.length === 0,
        })}
      >
        <ScrollArrowButton disabled={!pagination.hasNextPage} onClick={() => pagination.goToNextPage()}>
          <ChevronDownIcon strokeWidth={2} />
        </ScrollArrowButton>
        <ScrollArrowButton disabled={!pagination.hasPreviousPage} onClick={() => pagination.goToPreviousPage()}>
          <ChevronUpIcon strokeWidth={2} />
        </ScrollArrowButton>
      </div>
    </div>
  );
}

function ScrollArrowButton({
  className,
  children,
  ...props
}: React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>) {
  return (
    <button
      {...props}
      type="button"
      className={classNames(
        "flex flex-1 justify-center rounded-xl bg-slate-50 p-2",
        "dark:bg-slate-800/70 dark:text-slate-200",
        "disabled:cursor-not-allowed disabled:text-slate-300",
        className,
      )}
    >
      {children}
    </button>
  );
}
